import { useEffect, useRef, useState } from "react"

import { GROUP } from "@shared/config"
import { Account, Schedule } from "@shared/contexts"
import { auth, getParam, getTimestamp, kick } from "@shared/modules"
import { streamerService, viewerService } from "@shared/services"

interface Props {
  schedule: Schedule | null
  account: Account
  setAccount: React.Dispatch<React.SetStateAction<Account>>
  isDetect: boolean
}

export default function useWatchingViewer({
  schedule,
  account,
  setAccount,
  isDetect,
}: Props): void {
  const scheduleKey = schedule?.key
  const paymentInfo = schedule?.paymentInformation
  const viewer = account.viewer
  const fbUniqueId = account.currentUser?.uid
  const viewerRole = viewer?.role

  const isOnlyPlayer = getParam("is_only_player") === "true"

  const [watchingViewerRef, setWatchingViewerRef] = useState<
    firebase.database.Reference | undefined
  >()
  const isDisConnectedRef = useRef<boolean>()

  useEffect(() => {
    if (!watchingViewerRef || !isDetect || !viewerRole) return

    watchingViewerRef!.on(
      "child_changed",
      (snapshot: firebase.database.DataSnapshot) => {
        if (snapshot.key === "timestamp") {
          if (snapshot.val() === getTimestamp()) return
          ;(async () => {
            if (
              GROUP.STREAMER.includes(viewerRole) &&
              isDisConnectedRef.current
            )
              return streamerService.firebaseDisconnected(viewerRole)

            streamerService.duplicateUser(viewerRole)

            await auth().signOut()
            kick(
              isDisConnectedRef.current
                ? "채팅서버와 연결이 끊어졌습니다. 재입장 부탁 드립니다."
                : "중복 로그인이 감지되었습니다.",
            )
          })()
        } else if (snapshot.key === "is_blocked_chat") {
          setAccount((prevAccount: Account) => ({
            ...prevAccount,
            viewer: {
              ...prevAccount.viewer!,
              isBlockedChat: snapshot.val(),
            },
          }))

          if (!snapshot.val()) return

          alert("채팅금지 되었습니다.")
        }
      },
    )

    watchingViewerRef!.on(
      "child_added",
      (snapshot: firebase.database.DataSnapshot) => {
        if (snapshot.key === "is_kicked" && snapshot.val()) {
          kick("강제퇴장 되었습니다.")
        }
      },
    )

    return () => {
      watchingViewerRef.off("child_changed")
      watchingViewerRef.off("child_added")
    }
  }, [viewerRole, isDetect, watchingViewerRef, setAccount])

  useEffect(() => {
    if (
      !scheduleKey ||
      !paymentInfo ||
      !fbUniqueId ||
      !isDetect ||
      watchingViewerRef ||
      isOnlyPlayer
    )
      return

    viewerService.detectConnection(
      scheduleKey,
      paymentInfo,
      fbUniqueId,
      viewer!,
      setWatchingViewerRef,
      isDisConnectedRef,
    )
  }, [
    scheduleKey,
    paymentInfo,
    fbUniqueId,
    viewer,
    watchingViewerRef,
    isDetect,
    isOnlyPlayer,
  ])
}
